import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import type { PagedResponse } from '@angeltrax/ngx-aui';
import { ParamSerializer } from '../generic/param-serializer';
import { environment } from 'src/environments/environment';
import type { CreateModeSuggestionPayload, EditModeSuggestionPayload, MinimalStop, MinimalStopQueryOpts, Stop, StopCreatePayload, StopQueryOpts, StopResetToImportPayload, StopSuggestion, StopUpdatePayload } from './stop';

@Injectable()

export class StopsService {

  protected apiUrl = `${environment.apiBaseUrl}/core/stops`;

  public constructor(
    @Inject(HttpClient) protected readonly http: HttpClient,
    @Inject(ParamSerializer) protected readonly paramSerializer: ParamSerializer,
  ) {}

  public searchMinimalStops(filters: MinimalStopQueryOpts): Observable<PagedResponse<MinimalStop>> {
    const serializedParams = this.paramSerializer.serialize(filters);
    const url = `${this.apiUrl}?${serializedParams}`;

    return this.http.get<MinimalStop[]>(url, { observe: 'response' }).pipe(
      map(response => ({
        data: response.body ?? [],
        count: parseInt(response.headers.get('X-RESULT-COUNT')!, 10),
      })),
    );
  }

  public search(filters: StopQueryOpts): Observable<PagedResponse<Stop>> {
    const serializedParams = this.paramSerializer.serialize(filters);
    const url = `${this.apiUrl}?${serializedParams}`;

    return this.http.get<Stop[]>(url, { observe: 'response' }).pipe(
      map(response => ({
        data: response.body ?? [],
        count: parseInt(response.headers.get('X-RESULT-COUNT')!, 10),
      })),
    );
  }

  public create(payload: StopCreatePayload): Observable<Stop> {
    return this.http.post<Stop>(this.apiUrl, payload);
  }

  public createBulk(payloads: StopCreatePayload[]): Observable<Stop[]> {
    return this.http.post<Stop[]>(this.apiUrl, payloads);
  }

  public update(id: string, payload: StopUpdatePayload): Observable<Stop> {
    return this.http.put<Stop>(`${this.apiUrl}/${id}`, payload);
  }

  public delete(id: string): Observable<void> {
    return this.http.delete(`${this.apiUrl}/${id}`) as unknown as Observable<void>;
  }

  public resetToImport(id: string, payload: StopResetToImportPayload): Observable<Stop> {
    return this.http.post<Stop>(`${this.apiUrl}/${id}/reset-to-import`, payload);
  }

  public getSuggestions(
    payload: CreateModeSuggestionPayload | EditModeSuggestionPayload,
  ): Observable<StopSuggestion[]> {
    return this.http.post<StopSuggestion[]>(`${this.apiUrl}/suggestions`, payload);
  }

}
